import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X, Phone, MessageCircle, Facebook, Instagram, Mail } from 'lucide-react';

const WHATSAPP_NUMBER = '529993607922';
const WHATSAPP_MESSAGE = encodeURIComponent('¡Hola! Me interesa rentar un inflable para mi evento.');
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0d3815ce-2fa6-4430-b5d2-0237895e78e6/artifacts/wq5yg206_logoAllegra.png';

export default function PublicLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Allegra Brincolines" className="h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <NavLink 
                to="/" 
                className={({ isActive }) => `
                  font-medium transition-colors
                  ${isActive ? 'text-primary' : 'text-allegra-gray hover:text-allegra-dark'}
                `}
              >
                Inicio
              </NavLink>
              <NavLink 
                to="/catalogo" 
                className={({ isActive }) => `
                  font-medium transition-colors
                  ${isActive ? 'text-primary' : 'text-allegra-gray hover:text-allegra-dark'}
                `}
              >
                Catálogo
              </NavLink>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="rounded-full gap-2 bg-primary hover:bg-primary/90" data-testid="whatsapp-header-btn">
                  <MessageCircle className="w-4 h-4" />
                  Cotizar Ahora
                </Button>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-nav-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <NavLink 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="block font-medium text-allegra-dark"
              >
                Inicio
              </NavLink>
              <NavLink 
                to="/catalogo" 
                onClick={() => setMobileMenuOpen(false)}
                className="block font-medium text-allegra-dark"
              >
                Catálogo
              </NavLink>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full rounded-full gap-2 bg-primary" data-testid="whatsapp-mobile-btn">
                  <MessageCircle className="w-4 h-4" />
                  Cotizar por WhatsApp
                </Button>
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-allegra-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-white rounded-xl p-2">
                  <img src={LOGO_URL} alt="Allegra Brincolines" className="h-16 w-auto" />
                </div>
              </div>
              <p className="text-white/70">
                Renta de inflables para fiestas infantiles. 
                ¡Diversión garantizada para los más pequeños!
              </p>
              <div className="flex items-center gap-3 mt-4">
                <a 
                  href="https://www.facebook.com/people/Allegra-Brincolines/61582449267780/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="facebook-link"
                >
                  <Facebook className="w-5 h-5 text-white" />
                </a>
                <a 
                  href="https://www.instagram.com/allegrabrincolines"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram className="w-5 h-5 text-white" />
                </a>
                <a 
                  href="mailto:allegrabrincolines@gmail.com"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="email-link"
                >
                  <Mail className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-bold text-lg mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Inicio</Link></li>
                <li><Link to="/catalogo" className="text-white/70 hover:text-white transition-colors">Catálogo</Link></li>
                <li><Link to="/admin/login" className="text-white/70 hover:text-white transition-colors">Acceso Admin</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-lg mb-4">Contacto</h4>
              <div className="space-y-3">
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp: 999 360 7922
                </a>
                <a 
                  href="tel:+529993607922"
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  +52 999 360 7922
                </a>
                <a 
                  href="mailto:allegrabrincolines@gmail.com"
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  allegrabrincolines@gmail.com
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-white/50">
            <p>© {new Date().getFullYear()} Allegra Brincolines. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button */}
      <a 
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-float-btn"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
